<template>
    <div @click="disablePopupHandler" @focusin="disablePopupHandler">
        <template v-if="!vehicle">
            <div class="container">
                <div class="relative">
                    <used-details-header></used-details-header>

                    <div class="loading">
                        <img src="/images/shared/Robins-And-Day-Loader.svg" alt="Loading Details" />
                    </div>
                </div>
            </div>
        </template>
        <template v-else>
            <!-- Campaign: 2025 August Home Charger -->
            <div v-if="hasHomeChargerOffer" class="event-banner__container">
                <picture>
                    <source media="(min-width: 768px)" type="image/webp" srcset="/images/events/2025/home-charger/thin-banner-desktop@1x.webp, /images/events/2025/home-charger/thin-banner-desktop@2x.webp 2x">
                    <source media="(min-width: 768px)" srcset="/images/events/2025/home-charger/thin-banner-desktop@1x.webp, /images/events/2025/home-charger/thin-banner-desktop@2x.webp 2x">
                    <source type="image/webp" srcset="/images/events/2025/home-charger/thin-banner-mobile@1x.webp, /images/events/2025/home-charger/thin-banner-mobile@2x.webp 2x">
                    <img class="img-responsive" srcset="/images/events/2025/home-charger/thin-banner-mobile@1x.jpg, /images/events/2025/home-charger/thin-banner-mobile@2x.jpg 2x" style="width:100%" alt="">
                </picture>
            </div>

            <VehicleDetails
                :vehicle="vehicle"
                :monthly-price="monthlyPrice"
                :apr-rate="formattedApr"
                :deposit-contribution="formattedDepositContribution"
                :dealer-name="dealerName"
                :tel="tel"
                :opening-hours="openingHours"
                :has-live-chat="hasLiveChat"
                :is-sold="isSold"
                :is-reserved="isReservedOrSold"
                :buy-online-disabled="buyOnlineDisabled"
                :reserve-online-disabled="reserveOnlineDisabled"
                :have-finance="haveFinance"
                :last-search-page="lastSearchPage"
                @scroll="scrollTo('fullSpec')"
                @scroll-finance="scrollTo('financeCalculator')"
                @scroll-ev-calculator="scrollTo('electricVehicleChargeCalculator')"
                @book-a-test-drive="scrollTo('testDrive')"
                @show-contact="scrollTo('contactDealerForm')"
                @show-care-plan="scrollTo('carePlan')"
                @show-enquiry-form="scrollTo('contactDealerForm')"
                @show-similar-stock="scrollTo('similarStock')"
                :custom-event-category="customEventCategory"
                >
                <template v-slot:gallery>
                    <AutosOnShowGallery
                        v-if="vehicle.aos_player"
                        :vehicle="vehicle"
                        :is-reserved="isReservedOrSold"
                        v-on:scroll-to="scrollToFinance"
                        :custom-event-category="customEventCategory"
                    />
                    <SlickGallery
                        v-else
                        :vehicle="vehicle"
                        :is-reserved="isReservedOrSold"
                        v-on:scroll-to="scrollToFinance"
                        :custom-event-category="customEventCategory"
                    />
                </template>
            </VehicleDetails>

            <SimilarStock
                v-if="vehicle && (isReserved || isSold)"
                :vehicle="vehicle"
                ref="similarStock"
                :custom-event-category="customEventCategory"
            />

            <FullSpec
                v-if="vehicle.used_vehicle_features.length > 0"
                :vehicle="vehicle"
                ref="fullSpec"
                :custom-event-category="customEventCategory"
            />

            <FinanceCalculator
                v-if="vehicle && !isReserved && haveFinance"
                id="personalise-finance"
                class="personalise-finance"
                ref="financeCalculator"
                v-on:have-finance-updated="haveFinanceUpdated"
                v-on:finance-updated="financeUpdated"
                :vehicle="vehicle"
                :apply-online-disabled="applyOnlineDisabled"
                :is-reserved="isReservedOrSold"
                :reserve-online-disabled="reserveOnlineDisabled"
                v-on:contact-dealer="contactDealerOpen = !contactDealerOpen"
                :custom-event-category="customEventCategory"
            />

            <div class="bg">
                <ElectricVehicleChargeCalculator
                    v-if="vehicle && vehicle.fuel_type === 'Electric'"
                    :vehicle="vehicle"
                    ref="electricVehicleChargeCalculator"
                />
            </div>

            <div class="bg bg--light-blue">
                <ContactDealerForm
                    v-if="vehicle"
                    :vehicle="vehicle"
                    ref="contactDealerForm"
                />
            </div>

            <div class="bg">
                <div class="container">
                    <valuation-tool v-if="vehicle" :use-specific-dealer="vehicle.location.id" :used-vehicle-id="vehicle.id" />
                </div>
            </div>

            <Benefits
                :vehicle="vehicle"
            />

            <CarCarePlan
                ref="carePlan"
                v-if="hasLifetimeWarranty"
                :custom-event-category="customEventCategory"
            />

            <TestDriveForm
                v-if="vehicle.location && !vehicle.location.is_national_centre && !isReserved"
                :vehicle="vehicle"
                ref="testDrive"
            />

            <div class="container">
                <p class="terms">*Free WallBox charger with selected battery electric vehicles when purchased with a Personal Contract Purchase or Conditional Sale finance agreement. Please contact your local retailer to clarify eligible and available vehicles. Offer valid from 14th October 2024 to 31st December 2025. <a href="/terms-and-conditions" target="_blank">T&Cs and exclusions apply.</a></p>
            </div>

            <GetInTouch
                :dealer-name="dealerName"
                :tel="tel"
                :url="`/${vehicle.location.url}`"
                @openLiveChat="openLiveChat"
                :custom-event-category="customEventCategory"
            />

            <reserve-online-popup
                ref="remodal"
                data-remodal-id="reserve_modal"
                :disabled="disablePopup || isReserved"
                :vehicle="vehicle"
            />
        </template>
    </div>
</template>

<script>
import UsedDetailsHeader from "../components/ContentLoaders/UsedDetailsHeader.vue";
import AutosOnShowGallery from "../components/AutosOnShowGallery.vue";
import SlickGallery from "../components/SlickGallery.vue";
import ValuationTool from "../../valuation-tool/ValuationTool.vue";
import FinanceCalculator from "../components/Finance/FinanceCalculator.vue";
import ContactDealerForm from "../components/ContactDealerForm.vue";
import ReserveOnlinePopup from "./../components/ReserveOnline/ReserveOnlinePopup.vue";
import VehicleDetails from "../components/VehicleDetails.vue";
import FullSpec from "../components/FullSpec.vue";
import Benefits from "../components/UsedDetails/Benefits.vue";
import GetInTouch from "../components/GetInTouch.vue";
import CarCarePlan from "../components/UsedDetails/CarCarePlan.vue";
import TestDriveForm from '../components/UsedDetails/TestDriveForm.vue';
import SimilarStock from '../components/UsedDetails/SimilarStock.vue';
import { Tabs, Tab } from "uiv";
import { mapState } from 'vuex';
import { addOrIncrementToViewHistory } from "../../usedVehicleViewHistoryLocalStorage";
import arrayChunk from "../../modules/arrayChunk";
import strSlug from "../../modules/strSlug";
import numberFormat from "../api/NumberFormat";
import getFinanceNameFromKey from "../api/finance/GetFinanceNameFromKey";
import ElectricVehicleChargeCalculator from "../components/UsedDetails/ElectricVehicleChargeCalculator.vue";

const VueScrollTo = require("vue-scrollto");
Vue.use(VueScrollTo);

export default {
    components: {
        UsedDetailsHeader,
        AutosOnShowGallery,
        SlickGallery,
        Tabs,
        Tab,
        ValuationTool,
        FinanceCalculator,
        ContactDealerForm,
        ReserveOnlinePopup,
        VehicleDetails,
        FullSpec,
        Benefits,
        GetInTouch,
        CarCarePlan,
        TestDriveForm,
        SimilarStock,
        ElectricVehicleChargeCalculator
    },
    props: {
        id: {
            required: true,
        },
    },
    data() {
        return {
            vehicle: false,
            arrangeATestDriveOpen: false,
            contactDealerOpen: false,
            servicePlanOpen: false,
            haveFinance: true,
            quote: "loading",
            pusher: false,
            channel: false,
            animateViewCount: false,
            hasLiveChat: false,
            disablePopup: false,
            reservationFormCompletedByCustomer: false,
            customEventCategory: 'Used Vehicles - More Details',
            revealEvent:false,
        };
    },
    methods: {
        disablePopupHandler(e) {
            if (!this.disablePopup) {
                switch (e.target.tagName) {
                    case "BUTTON":
                        this.disablePopup = true;
                        break;
                    case "INPUT":
                        this.disablePopup = true;
                        break;
                    case "SELECT":
                        this.disablePopup = true;
                        break;
                    case "TEXTAREA":
                        this.disablePopup = true;
                        break;
                    case "LABEL":
                        this.disablePopup = true;
                        break;
                    default:
                        return;
                }
            }
        },
        openLiveChat() {
            window.liveChatClass.startChat(this.vehicle.location);
            // window.openLiveChat();
        },
        strSlug,
        arrayChunk,
        scrollTo(target) {
            if (typeof target == "undefined" || typeof this.$refs[target] == "undefined") {
                return;
            }
            this.$refs[target].$el.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        },
        haveFinanceUpdated(haveFinance) {
            this.haveFinance = haveFinance;
        },
        financeUpdated(newQuote) {
            this.quote = newQuote;
        },
        scrollToFinance(source) {
            if (source != "") {
                this.gaTag('Used Vehicle', 'Scroll To Finance', source);
            }
            VueScrollTo.scrollTo(this.$refs["financeCalculator"].$el, 500, {
                onDone: () => {
                    document.location.hash = "#personalise-finance";
                },
            });
        },
    },
    computed: {
        ...mapState("BuyOnline", [
            'locked',
            'lockedByCustomer',
            'lockedReason',
            'lockedButReservable',
            'lockedButApplyForFinancable',
            'lockedButPurchasable',
            'vyneAvailable',
            'autoconvertAvailable',
        ]),
        buyOnlineDisabled() {
            if (this.vyneAvailable == false && this.autoconvertAvailable == false) {
                return true;
            }

            if (this.locked == false) {
                return false;
            }

            return !this.lockedButPurchasable;
        },
        isSold() {
            if (this.locked == false) {
                return false;
            }
            if (this.lockedReason == 'sold') {
                return true;
            }
            return false;
        },
        isReserved() {
            if (this.locked == false) {
                return false;
            }

            if (this.lockedReason == 'reserved') {
                return true;
            }
            return false;
        },
        isReservedOrSold() {
            return (this.isReserved || this.isSold);
        },
        reserveOnlineDisabled() {
            if (this.locked == false) {
                return false;
            }

            return !this.lockedButReservable;
        },
        applyOnlineDisabled() {
            if (this.autoconvertAvailable == false) {
                return true;
            }
            if (this.locked == false) {
                return false;
            }

            return !this.lockedButApplyForFinancable;
        },
        openingHours() {
            if (!this.vehicle || !this.vehicle.location) {
                return [];
            }

            const data = JSON.parse(this.vehicle.location.opening_times);
            if (typeof data.Sales == "undefined") {
                return [];
            }

            return data.Sales;
        },

        whyTitle() {
            return "Why Stellantis &You UK";
        },
        financeQuote() {
            if (
                typeof this.quote.finance == "undefined" ||
                typeof this.quote.finance.FinanceProductResults == "undefined" ||
                typeof this.quote.finance.FinanceProductResults[0] == "undefined" ||
                typeof this.quote.finance.FinanceProductResults[0].Quote == "undefined" ||
                this.quote.finance.FinanceProductResults[0].HasError == true
            ) {
                return false;
            }

            if (typeof this.quote.finance.FinanceProductResults[0].Quote.AllInclusiveRegularPayment == "undefined") {
                return false;
            }

            return this.quote.finance.FinanceProductResults[0].Quote;
        },
        monthlyPrice() {
            if (this.quote == "loading") {
                return "loading";
            }

            if (!this.financeQuote) {
                return false;
            }

            return "&pound;" + numberFormat(this.financeQuote.AllInclusiveRegularPayment, 0);
        },
        depositContribution() {
            if (!this.financeQuote) {
                return 0.00;
            }

            if (typeof this.financeQuote.DepositContribution !== 'undefined') {
                return parseFloat(this.financeQuote.DepositContribution);
            }
            if (typeof this.financeQuote.DepositContributions == 'undefined') {
                return 0.00;
            }

            let depCon = 0.00;
            for (let index = 0, len= this.financeQuote.DepositContributions; index < len; index++) {
                const financeNotification = this.financeQuote.DepositContributions[index];
                if (typeof financeNotification.Amount == 'undefined') {
                    continue;
                }
                depCon += parseFloat(financeNotification.Amount);
            }

            return depCon;
        },
        apr() {
            if (!this.financeQuote) {
                return null;
            }

            if (typeof this.financeQuote.Apr == 'undefined') {
                return null;
            }

            return parseFloat(this.financeQuote.Apr);
        },
        financeName() {
            if (this.monthlyPrice == false || this.monthlyPrice == "loading") {
                return "Finance";
            }
            return getFinanceNameFromKey(this.vehicle, this.quote.finance.FinanceProductResults[0].Key, true);
        },
        dealerName() {
            if (!this.vehicle || !this.vehicle.used_dealer) {
                return "";
            }
            return this.vehicle.used_dealer.long_name ? this.vehicle.used_dealer.long_name : this.vehicle.location.name;
        },
        tel() {
            if (!this.vehicle) {
                return "";
            }
            return this.vehicle.location.tel_used ? this.vehicle.location.tel_used : this.vehicle.location.tel_general;
        },
        hasLifetimeWarranty() {
            if (!this.vehicle) {
                return false;
            }
            if (this.vehicle.age < 2) {
                return false;
            }
            if (this.vehicle.age > 10) {
                return false;
            }
            if (this.vehicle.mileage > 100000) {
                return false;
            }
            return true;
        },

        formattedPriceSaved() {
            return '£' + numberFormat(this.vehicle.price_save);
        },

        formattedApr() {
            if (!this.apr) {
                return null;
            }
            return this.apr.toFixed(1) + '% APR';
        },

        formattedDepositContribution() {
            if (!this.depositContribution && this.depositContribution <= 0) {
                return null;
            }

            return '£' + numberFormat(this.depositContribution);
        },

        extraDataArray() {
            if (this.vehicle.extra_data.length == 0) {
                return [];
            }

            return this.vehicle.extra_data.split(' ');
        },
        hasHomeChargerOffer() {
            if (!this.vehicle) {
                return false;
            }
            if (this.extraDataArray.length == 0) {
                return false;
            }
            return this.extraDataArray.includes('wallbox-offer');
        },
    },
    created() {
        addOrIncrementToViewHistory(this.id);

        this.$store.dispatch("BuyOnline/loadVehicleIfNecessary", this.id)
            .then(vehicle => {
                if (vehicle.is_new != 0) {
                    this.$router.push({ name: "new-cars-in-stock-details", params: { id: vehicle.id, slug: vehicle.slug } });
                    return;
                }

                if (vehicle.is_sold) {
                    this.disablePopup = true;
                }

                this.vehicle = vehicle;

                window.liveChatClass.setVehicleData({
                    year: vehicle.vehicle_year_of_manufacture,
                    manufacturer: vehicle.manufacturer,
                    model: vehicle.model,
                    reg_no: vehicle.reg_no,
                    new_or_used: "used",
                    id: vehicle.id,
                    trim: vehicle.trim,
                    title: vehicle.version,
                    price: vehicle.price,
                    fuel_type: vehicle.fuel_type,
                });
                window.liveChatClass.initLiveChat(vehicle.location.id);
                this.$store.commit("Layout/setBrand", this.vehicle.brand);

                // Send a remarketing tag
                dataLayer.push(
                    {
                        event: "fireRemarketingTag",
                        google_tag_params: {
                            dynx_itemid: vehicle.id,
                            dynx_pagetype: "used",
                        },
                    },
                    {
                        event: "view_item",
                        value: vehicle.price,
                        items: [
                            {
                                id: vehicle.id,
                                google_business_vertical: "retail",
                            },
                        ],
                    }
                );

                // Campaign
                // this.$store.commit('CampaignStore/setVehicle', this.vehicle);
                // If it's a Vauxhall page, remove the live chat
                // if (this.vehicle.brand == 'vauxhall') {
                // document.querySelector('body').classList.add('no-live-chat');
                // if (vehicle.location) {
                //     liveChatClass.initLiveChat(vehicle.location.id);
                // }
                // }
                // Update title tag
                document.querySelector("title").innerHTML =
                    "Used " +
                    vehicle.manufacturer +
                    " " +
                    vehicle.model +
                    " " +
                    vehicle.body +
                    " " +
                    vehicle.version +
                    " " +
                    vehicle.transmission +
                    " " +
                    vehicle.short_colour +
                    " " +
                    vehicle.reg_no +
                    " | Used Cars " +
                    this.dealerName;
                })
                .catch((error) => {
                    console.error(error);
                    this.$router.push({ name: "used-landing" });
                });
        },

    mounted() {
        setTimeout(() => {
            if (window.location.hash === "#personalise-finance") {
                this.scrollTo('financeCalculator');
            }
        }, 500);
    },

    beforeDestroy() {
        this.$store.commit("Valuations/clearValuation");
    },

    watch: {
        reserveOnlineDisabled: {
            handler: function(newValue) {
                this.disablePopup = newValue;
            },
            instant: true,
            deep: false,
        },
    },

    beforeRouteLeave(to, from, next) {
        if (to.name == 'buy-online-landing') {
            this.gaTag('Used Vehicle Details', 'Buy Online', 'Clicked');
        } else if (to.name == 'finance-application') {
            if (to.params.applicationType == 'soft') {
                this.gaTag('Used Vehicle Details', 'Free Credit Check', 'Clicked');
            } else {
                this.gaTag('Used Vehicle Details', 'Apply For Finance', 'Clicked');
            }
        }
        next();
    }
};
</script>


<style lang="scss">
@import "../../../sass/redesign/variables";
.personalise-finance {
    margin-top: 45px;
}

</style>



// WEBPACK FOOTER //
// resources/assets/js/used-vue/views/used-details.vue